/*
    This is a fluent builder DSL to construct formatted SQL queries.
    It's built to create simple queries meant as example queries,
    and does not focus on feature completeness or input safety.
    Extend the builder only when it's needed.

    See tests for usage.
*/

export type SqlBuilderOptions = {
    indent?: string;
    comment?: string;
};

type SelectOptions = {
    top?: number;
};

type JoinOptions = {
    type?: string;
};

export class SqlBuilder {
    readonly indent: string;
    query: string;

    constructor(options?: SqlBuilderOptions) {
        this.indent = options?.indent ?? '';
        this.query = options?.comment ? `${this.indent}-- ${options.comment}\n` : '';
    }

    select(columns: string[], options?: SelectOptions): SqlBuilder {
        const top = options?.top ? `TOP (${options.top}) ` : '';
        this.query += `${this.indent}SELECT ${top + columns.join(`\n${this.indent}    , `)}\n`;
        return this;
    }

    from(table: string): SqlBuilder {
        this.query += `${this.indent}  FROM ${table}\n`;
        return this;
    }

    join(expression: string, options?: JoinOptions): SqlBuilder {
        const joinType = options?.type ? options.type.toUpperCase() + ' ' : '';
        this.query += `${this.indent}  ${joinType}JOIN ${expression}\n`;
        return this;
    }

    where(condition: string): SqlBuilder {
        this.query += `${this.indent}  WHERE ${condition}\n`;
        return this;
    }

    and(condition: string): SqlBuilder {
        this.query += `${this.indent}    AND ${condition}\n`;
        return this;
    }

    groupBy(condition: string[]): SqlBuilder {
        this.query += `${this.indent}  GROUP BY ${condition.join(', ')}\n`;
        return this;
    }

    orderBy(terms: string[]): SqlBuilder {
        this.query += `${this.indent}  ORDER BY ${terms.join(', ')}\n`;
        return this;
    }

    asSubQuery(alias: string): string {
        return `(\n${this.build()}) ${alias}`;
    }

    build(): string {
        return this.query.trimEnd();
    }
}

export function makeSql(options?: SqlBuilderOptions): SqlBuilder {
    return new SqlBuilder(options);
}
